import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useCrmResource } from "../../../hooks/useCrmResource";
import { listTickets } from "../../../api/crmTickets";
import { TicketSummary } from "../../../api/crmTypes";
import { CrmConnectGate } from "../../../context/CrmSessionContext";
import { fmtDate } from "../../../types/promos";
import CreateTicketModal from "../../../components/crm/CreateTicketModal";
import TicketPipelineSelect from "../../../components/crm/TicketPipelineSelect";
import TicketStageSelect from "../../../components/crm/TicketStageSelect";
import Section from "./Section";
import Pill from "./Pill";

/**
 * Panel "Tickets del CRM" del aside de la ficha del lead (Fase 2 de la
 * integración CRM). Lista los tickets del lead desde el BFF del CRM
 * (GET /tickets?leadId=...) y permite crear uno nuevo pre-vinculado al lead.
 *
 * El CRM usa el MISMO ULID del lead que Operations como PK (leadId acepta hex
 * 32 o Crockford 26), así que pasamos `leadUlid` tal cual. GET /tickets nunca
 * devuelve 404: un lead desconocido o sin proyectar en el CRM devuelve
 * { items: [], total: 0 }, indistinguible de "sin tickets" (estado vacío).
 *
 * Pide `archived: 'include'` para que un archivado siga siendo ALCANZABLE desde
 * la ficha, igual que el tab Tickets del CRM (LeadTicketsTab). Sin esto el
 * default del BFF es 'exclude' y Operations no veía siquiera que el ticket
 * existía: archivar era un viaje de ida y el ticket solo se recuperaba por URL
 * directa, que es justo lo que no tienes cuando lo buscas (incidente
 * 2026-07-16, crm/docs/DECISIONS.md).
 */
export default function LeadTickets({ leadUlid }: { leadUlid: string }) {
  return (
    <Section title="Tickets (CRM)" accent="#7048e8" defaultOpen>
      <CrmConnectGate>
        <LeadTicketsInner leadUlid={leadUlid} />
      </CrmConnectGate>
    </Section>
  );
}

const TICKETS_LIMIT = 100;

function LeadTicketsInner({ leadUlid }: { leadUlid: string }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // items/loading/error/reload con guard de cancelación, silenciado de 401 y
  // reset síncrono al cambiar de lead — compartido con el resto de paneles CRM.
  const fetchTickets = useCallback(
    () =>
      listTickets({
        leadId: leadUlid,
        limit: TICKETS_LIMIT,
        archived: "include",
      }),
    [leadUlid],
  );
  const {
    data,
    loading,
    error,
    reload: load,
  } = useCrmResource(
    fetchTickets,
    [leadUlid],
    "No se pudieron cargar los tickets del CRM",
  );
  const tickets = data?.items ?? null;
  const total = data?.total ?? 0;

  if (loading && tickets === null) {
    return <Muted>Cargando tickets…</Muted>;
  }

  if (error) {
    return (
      <div>
        <div
          style={{
            background: "#fde2e2",
            color: "#b32424",
            padding: "0.5rem 0.7rem",
            borderRadius: "0.4rem",
            fontSize: ".82rem",
            marginBottom: "0.6rem",
          }}
        >
          {error}
        </div>
        <button type="button" onClick={load} style={retryBtn}>
          Reintentar
        </button>
      </div>
    );
  }

  const items = tickets ?? [];
  // `archived` es ORTOGONAL a open/closed (un archivado conserva su stage): se
  // separa ANTES para que no aparezca además entre los abiertos/cerrados.
  // Mismo criterio que el LeadTicketsTab del CRM.
  const live = items.filter((t) => !t.archived);
  const archived = items.filter((t) => t.archived);
  const open = live.filter((t) => t.stageState === "open");
  const closed = live.filter((t) => t.stageState === "closed");
  // Solo desglosamos abiertos/cerrados si tenemos la lista completa; con un
  // truncado a TICKETS_LIMIT el desglose sería engañoso, mostramos el total.
  const complete = items.length === total;
  const summary = complete
    ? `${open.length} abiertos · ${closed.length} cerrados` +
      (archived.length > 0 ? ` · ${archived.length} archivados` : "")
    : `${total} tickets`;
  const truncated = total > items.length;

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          marginBottom: "0.7rem",
        }}
      >
        <span style={{ color: "#73879C", fontSize: ".78rem" }}>{summary}</span>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          style={addBtn}
        >
          + Crear ticket
        </button>
      </div>

      {items.length === 0 && <Muted>Sin tickets.</Muted>}

      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {open.map((t) => (
          <TicketRow key={t.id} ticket={t} onMoved={load} />
        ))}
        {closed.map((t) => (
          <TicketRow key={t.id} ticket={t} onMoved={load} />
        ))}
      </ul>

      {/* Archivados: presentes pero replegados (como el CRM). Que existan es
          dato de negocio; que estorben, no. Restaurar se hace desde la ficha
          del ticket, a un clic desde aquí. */}
      {archived.length > 0 && (
        <div style={{ marginTop: "0.4rem" }}>
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            style={disclosureBtn}
          >
            {showArchived ? "▾" : "▸"} Archivados ({archived.length})
          </button>
          {showArchived && (
            <ul
              style={{
                listStyle: "none",
                margin: "0.5rem 0 0",
                padding: 0,
              }}
            >
              {archived.map((t) => (
                <TicketRow key={t.id} ticket={t} onMoved={load} archived />
              ))}
            </ul>
          )}
        </div>
      )}

      {truncated && (
        <div
          style={{
            color: "#73879C",
            fontSize: ".74rem",
            marginTop: "0.3rem",
          }}
        >
          {`Mostrando ${items.length} de ${total}`}
        </div>
      )}

      {createOpen && (
        <CreateTicketModal
          leadUlid={leadUlid}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            load();
          }}
        />
      )}
    </div>
  );
}

// Fila de ticket: enlace al detalle + cambio de stage inline. El <select> vive
// FUERA del <Link> (dentro navegaría al desplegarse) — por eso la tarjeta es un
// div y el enlace envuelve solo el texto. El pill de stage desaparece: lo dice
// ya el propio select, que además deja cerrarlo sin salir de la ficha.
//
// Un archivado se pinta en modo LECTURA: sin selects de pipeline/stage. Está
// fuera de los listados y del board, así que moverlo de stage no significaría
// nada; lo que toca antes es restaurarlo (desde la ficha del ticket).
function TicketRow({
  ticket,
  onMoved,
  archived = false,
}: {
  ticket: TicketSummary;
  onMoved: () => void;
  archived?: boolean;
}) {
  const closed = ticket.stageState === "closed";
  const [hover, setHover] = useState(false);
  return (
    <li style={{ marginBottom: "0.5rem" }}>
      <div
        style={{
          border: `1px solid ${hover ? "#adb5bd" : "#e9ecef"}`,
          borderRadius: "0.5rem",
          padding: "0.55rem 0.65rem",
          background: hover ? "#f8f9fb" : "white",
          // Un archivado se atenúa aunque su etapa siga abierta: lo que lo saca
          // de la vista de trabajo es el archivado, no la etapa.
          opacity: closed || archived ? 0.7 : 1,
          transition: "background 120ms ease, border-color 120ms ease",
        }}
      >
        <Link
          to={`/operations/tickets/${ticket.id}`}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{ display: "block", textDecoration: "none", color: "inherit" }}
        >
          <div
            style={{
              fontSize: ".85rem",
              fontWeight: 600,
              color: "#1f2933",
              marginBottom: "0.3rem",
            }}
          >
            {ticket.subject}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              flexWrap: "wrap",
              fontSize: ".72rem",
              color: "#73879C",
            }}
          >
            {archived ? (
              <>
                <Pill label="Archivado" scheme="amber" />
                {/* El stage lo diría el select, que aquí no está: sin esto un
                    archivado no mostraría en qué punto se quedó. */}
                <span>{ticket.stageLabel}</span>
              </>
            ) : (
              <Pill
                label={closed ? "Cerrado" : "Abierto"}
                scheme={closed ? "muted" : "green"}
              />
            )}
            {ticket.incidentType && (
              <Pill label={ticket.incidentType} scheme="muted" />
            )}
            {ticket.assignedTo && <span>{ticket.assignedTo}</span>}
            <span>{fmtDate(ticket.lastActivityAt)}</span>
          </div>
        </Link>
        {/* Pipeline encima de stage (jerarquía): al mover de pipeline, onMoved
            recarga la lista y el select de stage recibe el pipeline nuevo con
            su stage de aterrizaje (primer abierto del destino). */}
        {!archived && (
          <>
            <div style={{ marginTop: "0.4rem" }}>
              <TicketPipelineSelect
                ticketId={ticket.id}
                pipelineId={ticket.pipelineId}
                pipelineLabel={ticket.pipelineLabel}
                onMoved={onMoved}
                compact
              />
            </div>
            <div style={{ marginTop: "0.3rem" }}>
              <TicketStageSelect
                ticketId={ticket.id}
                pipelineId={ticket.pipelineId}
                stageId={ticket.stageId}
                stageLabel={ticket.stageLabel}
                onMoved={onMoved}
                compact
              />
            </div>
          </>
        )}
      </div>
    </li>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ color: "#73879C", fontSize: ".82rem" }}>{children}</div>
  );
}

const addBtn: React.CSSProperties = {
  padding: "0.3rem 0.65rem",
  borderRadius: "0.4rem",
  background: "#0069d9",
  color: "white",
  border: "1px solid #0069d9",
  fontSize: ".76rem",
  fontWeight: 600,
  cursor: "pointer",
};

// Desplegable de "Archivados": afordancia deliberadamente discreta (texto, no
// botón) — está para quien lo busca, no para competir con los tickets vivos.
const disclosureBtn: React.CSSProperties = {
  padding: 0,
  background: "none",
  border: "none",
  color: "#73879C",
  fontSize: ".74rem",
  fontWeight: 600,
  cursor: "pointer",
};

const retryBtn: React.CSSProperties = {
  padding: "0.35rem 0.75rem",
  borderRadius: "0.4rem",
  background: "white",
  color: "#0069d9",
  border: "1px solid #dee2e6",
  fontSize: ".8rem",
  fontWeight: 600,
  cursor: "pointer",
};
